import { FC, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';
import { STEPS_CONFIG } from '../helpers/wizard';
import { useWizard } from '../hooks/useWizard';


interface StepIndicatorProps {}

const WizardStepIndicator: FC<StepIndicatorProps> = () => {
  const dictionary = useDictionary();
  const { activeStepIndex } = useWizard();

  const steps = useMemo(() => {
    return STEPS_CONFIG.map((step, index) => ({
      title: dictionary.tributes.create.steps[step],
      isActive: index === activeStepIndex,
      isCompleted: index < activeStepIndex,
    }));
  }, [activeStepIndex, dictionary]);

  const stepOffsetPx = 18;

  const connectorWidth =  `calc(${(activeStepIndex / (steps.length - 1)) * 100}% - ${activeStepIndex * stepOffsetPx}px)`;

  return (
    <Box sx={{ width: '100%', mt: 3, mb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '500px',
          mx: 'auto',
          px: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Box
            id="connector-line"
            sx={{
              position: 'absolute',
              top: 20,
              left: '40px',
              right: '40px',
              height: '2px',
              bgcolor: '#e0e0e0',
              zIndex: 0,
            }}
          />

          {activeStepIndex > 0 && (
            <Box
              id="active-connector"
              sx={{
                position: 'absolute',
                top: 20,
                left: '40px',
                height: '2px',
                bgcolor: 'primary.main',
                zIndex: 1,
                transition: 'width 0.3s ease-in-out',
                width: connectorWidth,
              }}
            />
          )}

          {steps.map((step, index) => {
            const isCompleted = index < activeStepIndex;
            const isActive = index === activeStepIndex;

            return (
              <Box
                key={step.title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Box
                  id={`step-circle-${index + 1}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: isActive ? 'primary.main' : isCompleted ? 'primary.main' : 'white',
                    border: isActive || isCompleted ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
                    color: isActive || isCompleted ? 'white' : 'rgba(0, 0, 0, 0.6)',
                    fontWeight: 'bold',
                    mb: 1.5,
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {index + 1}
                </Box>

                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'primary.main' : 'textSecondary',
                    lineHeight: 1.2,
                    maxWidth: '80px',
                  }}
                >
                  {step.title}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default WizardStepIndicator;
